// PaymentIssueBanner.tsx: the in-app notice for a failed subscription payment.
//
// Self-contained: it fetches GET /billing/status itself and renders nothing
// unless an invoice is in dunning, so it can be mounted anywhere a signed-in
// user lands (the dashboard mount is added separately).
//
// The backend gives 3 days of grace from the first failed attempt
// (backend/internal/billing/webhook.go applyPaymentFailed). While the grace
// runs, the notice names the date access stops; after it, it says access is
// paused. "Update card" opens the Stripe Customer Portal. Dismissing hides the
// notice for this browser session only, and only for the state it was showing,
// so a dismissed grace notice still turns into the "paused" notice later.
import React, { useEffect, useState } from "react";
import { BillingStatus, getBillingStatus, openBillingPortalUrl } from "../api";
import { supabase } from "../lib/supabase";

const DISMISS_KEY_PREFIX = "parcely:paymentIssueDismissed:";

const PLAN_LABEL: Record<string, string> = { scout: "Scout", pro: "Pro", team: "Team" };

export function planLabel(plan: string): string {
  return PLAN_LABEL[plan] || "your plan";
}

export function formatGraceDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
}

// paymentIssueMessage is the notice's sentence for a status, or null when
// there is nothing to say.
export function paymentIssueMessage(status: BillingStatus): string | null {
  if (!status.paymentIssue) return null;
  const plan = planLabel(status.plan);
  if (status.accessPaused || !status.graceUntil) {
    return `Your ${plan} access is paused until your payment goes through.`;
  }
  return `Your last payment didn't go through. Update your card by ${formatGraceDate(status.graceUntil)} to keep ${plan}.`;
}

function dismissKey(status: BillingStatus): string {
  return DISMISS_KEY_PREFIX + (status.accessPaused ? "paused" : status.graceUntil || "");
}

function readDismissed(key: string): boolean {
  try {
    return sessionStorage.getItem(key) === "1";
  } catch {
    return false;
  }
}

const PaymentIssueBanner: React.FC = () => {
  const [status, setStatus] = useState<BillingStatus | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase.auth.getSession();
        if (!data.session) return;
        const s = await getBillingStatus();
        if (cancelled) return;
        setStatus(s);
        setDismissed(readDismissed(dismissKey(s)));
      } catch {
        // No notice is better than a broken one: billing state is also in
        // the dunning emails and the Customer Portal.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!status || dismissed) return null;
  const message = paymentIssueMessage(status);
  if (!message) return null;

  const dismiss = () => {
    try {
      sessionStorage.setItem(dismissKey(status), "1");
    } catch {
      // Storage blocked: dismiss for this page view only.
    }
    setDismissed(true);
  };

  const openPortal = async () => {
    setOpening(true);
    setError(null);
    try {
      window.location.href = await openBillingPortalUrl();
    } catch {
      setError("Couldn't open billing. Please try again.");
      setOpening(false);
    }
  };

  return (
    <div
      className="payment-issue-banner"
      role="alert"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        flexWrap: "wrap",
        padding: "10px 12px",
        margin: "0 0 12px",
        fontSize: "var(--text-sm, 14px)",
        lineHeight: 1.4,
        color: "var(--text-primary, #1e293b)",
        background: "var(--surface-warn, #fffbeb)",
        border: "1px solid var(--accent-warn, #f59e0b)",
        borderRadius: "var(--radius-md, 8px)",
      }}
    >
      <span style={{ flex: "1 1 240px" }}>
        {message}
        {error && <span style={{ display: "block", marginTop: 4 }}>{error}</span>}
      </span>
      {status.portalAvailable && (
        <button type="button" className="pricing-tier-cta pricing-tier-cta--primary" onClick={openPortal} disabled={opening}>
          {opening ? "Opening…" : "Update card"}
        </button>
      )}
      <button
        type="button"
        aria-label="Dismiss payment notice"
        onClick={dismiss}
        style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, lineHeight: 1, color: "inherit" }}
      >
        ×
      </button>
    </div>
  );
};

export default PaymentIssueBanner;
